/** SB-612: Initialize one message lifecycle that preserves content, live-region priority and
 * field descriptions. Associations use explicit markup or a single-field group.
 */
(() => {
  'use strict';
  if (window.sbFormAccessibility) return;
  window.sbFormAccessibility = true;
  const selector = '.form-message, .error_message, .success_message, .field-error, .field-success, .form-error, .form-success, .error-message, .success-message, .message_wrapper.error, #klaviyo-email-error, #klaviyo-email-success, [role="alert"], [role="status"]';
  const fields = 'input:not([type="hidden"]):not([type="submit"]):not([type="button"]), textarea, select';
  const pending = new Set();
  const associatedFields = new Map();
  let scheduled = false;
  let nextId = 0;
  /**
   * Classify known error markup without treating success messages as errors.
   * @param {Element} message - Message container to classify.
   * @returns {boolean} Whether the container represents an error.
   */
  const isError = message => !message.matches('.success_message, .field-success, .form-success, .success-message, .success, [id*="-success"]') &&
    (message.matches('.error_message, .field-error, .form-error, .error-message, .error, [id*="-error"]') ||
      !!message.querySelector('.icon--red-exclamation'));
  /**
   * Check that a message has content and is not hidden by itself or an ancestor.
   * @param {Element} message - Message whose active error state is being evaluated.
   * @returns {boolean} Whether the message is connected, nonempty and displayed.
   */
  function visible(message) {
    if (!message.isConnected || !message.textContent.replace(/^\s*Error:\s*/i, '').trim()) return false;
    for (let node = message; node; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (node.hidden || style.display === 'none' || style.visibility === 'hidden') return false;
    }
    return true;
  }
  /**
   * Retain explicit field descriptions or infer only an unambiguous field association.
   * @param {Element} message - Form message to associate without replacing help IDs.
   */
  function associate(message) {
    const form = message.closest('form');
    if (!form) return;
    let targets = Array.from(form.querySelectorAll(fields)).filter(field =>
      message.id && (field.getAttribute('aria-describedby') || '').split(/\s+/).includes(message.id));
    if (!targets.length) {
      const group = message.closest('.input-group, .field, .form-field');
      const groupFields = group ? Array.from(group.querySelectorAll(fields)) : [];
      if (groupFields.length === 1) targets = groupFields;
      else {
        const formFields = Array.from(form.querySelectorAll(fields));
        if (formFields.length === 1 && formFields[0].type === 'email') targets = formFields;
      }
    }
    if (!targets.length) return; // Never label every contact field with a form-wide error.
    if (!message.id) {
      do { message.id = 'sb-form-message-' + (++nextId); }
      while (document.querySelectorAll('[id="' + message.id + '"]').length > 1);
    }
    targets.forEach(field => {
      const ids = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      if (!ids.includes(message.id)) field.setAttribute('aria-describedby', [...ids, message.id].join(' '));
      if (!associatedFields.has(field)) associatedFields.set(field, new Set());
      associatedFields.get(field).add(message);
    });
  }
  /**
   * Add missing live semantics, one error prefix and field associations in place.
   * Existing message children and authored announcement priority are preserved.
   * @param {Element} message - Connected message container to enhance.
   */
  function enhance(message) {
    if (!message.isConnected) return;
    const error = isError(message);
    if (!message.hasAttribute('role') && !message.hasAttribute('aria-live')) {
      message.setAttribute('aria-live', 'polite');
    }
    if (!message.hasAttribute('aria-atomic')) message.setAttribute('aria-atomic', 'true');
    const prefix = message.querySelector('[data-sb-error-prefix]');
    if (prefix && (!error || !message.textContent.replace(/^\s*Error:\s*/i, '').trim())) prefix.remove();
    if (error && message.textContent.trim() && !/^error:/i.test(message.textContent.trim()) && !message.querySelector('.error-text-prefix, .error-prefix')) {
      const label = document.createElement('strong');
      label.className = 'error-text-prefix';
      label.setAttribute('data-sb-error-prefix', '');
      label.textContent = 'Error: ';
      message.prepend(label);
    }
    associate(message);
  }
  /** Recompute associated fields' invalid state from their active visible errors. */
  function updateInvalid() {
    associatedFields.forEach((knownMessages, field) => {
      if (!field.isConnected) return associatedFields.delete(field);
      const messages = (field.getAttribute('aria-describedby') || '').split(/\s+/)
        .map(id => document.getElementById(id)).filter(message => message?.matches(selector));
      const errors = [...new Set([...knownMessages, ...messages])].filter(isError);
      const invalid = errors.some(visible);
      if (invalid) {
        if (field.getAttribute('aria-invalid') !== 'true') field.setAttribute('aria-invalid', 'true');
      } else if (field.getAttribute('aria-invalid') === 'true') field.removeAttribute('aria-invalid');
    });
  }
  /**
   * Batch affected messages for enhancement and invalid-state reconciliation.
   * @param {Node} node - Changed node or subtree containing message containers.
   */
  function enqueue(node) {
    if (node.nodeType !== 1) node = node.parentElement;
    if (node) {
      const message = node.closest(selector);
      if (message) pending.add(message);
      node.querySelectorAll(selector).forEach(element => pending.add(element));
    }
    if (scheduled || !pending.size) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      const messages = Array.from(pending);
      pending.clear();
      messages.forEach(enhance);
      updateInvalid();
    });
  }
  /** Observe message changes and clear corrected newsletter format errors on input. */
  function initialize() {
    document.addEventListener('input', event => {
      const field = event.target;
      if (!(field instanceof HTMLInputElement) || !field.matches('#email_signup #k_id_email') ||
          !field.value.trim() || !field.validity.valid) return;
      const error = field.form?.querySelector('#klaviyo-email-error');
      if (!error || !visible(error)) return;
      // Klaviyo validates on submit. Clear only its stale email-format message
      // after correction; service failures still require an explicit retry.
      const message = error.textContent.replace(/^\s*Error:\s*/i, '').trim();
      if (message !== 'Invalid email address') return;
      error.style.display = 'none';
      error.textContent = '';
      updateInvalid();
    });
    enqueue(document.body);
    new MutationObserver(records => {
      records.forEach(record => {
        // Visibility may be controlled by an ancestor of the message.
        if (record.type === 'attributes') enqueue(record.target);
        else {
          enqueue(record.target);
          record.addedNodes.forEach(enqueue);
          if (record.removedNodes.length) updateInvalid();
        }
      });
    }).observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['hidden', 'style', 'class', 'role', 'aria-live', 'aria-atomic'] });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
